/**
 * Dependency-free horizontal-bar breakdown. Each row shows a label, a bar
 * scaled to the row's total (with the failed portion highlighted in red on
 * top of the base bar), and the raw counts. Pure CSS/flex — no SVG needed, no
 * external chart library.
 */

export interface BarBreakdownItem {
  key: string;
  label: string;
  count: number;
  failed?: number;
}

interface BarBreakdownProps {
  items: BarBreakdownItem[];
  /** Accent color for the "ok"/base portion of each bar. */
  color?: string;
  emptyLabel?: string;
}

export function BarBreakdown({
  items,
  color = '#f59e0b',
  emptyLabel = 'No data in window.',
}: BarBreakdownProps): JSX.Element {
  if (items.length === 0) {
    return <div className="py-6 text-center text-sm text-slate-500">{emptyLabel}</div>;
  }
  const max = Math.max(1, ...items.map((i) => i.count));

  return (
    <ul className="space-y-2" role="list">
      {items.map((it) => {
        const failed = it.failed ?? 0;
        const pct = (it.count / max) * 100;
        const failedPct = it.count > 0 ? (failed / it.count) * 100 : 0;
        return (
          <li key={it.key} className="min-w-0">
            <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
              <span className="min-w-0 truncate text-slate-300" title={it.label}>
                {it.label}
              </span>
              <span className="shrink-0 tabular-nums text-slate-400">
                {it.count.toLocaleString()}
                {failed > 0 && (
                  <span className="ml-1 text-red-400">({failed} failed)</span>
                )}
              </span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-800">
              <div
                className="flex h-full"
                style={{ width: `${pct}%` }}
                title={`${it.label}: ${it.count} total, ${failed} failed`}
              >
                <div
                  className="h-full"
                  style={{
                    width: `${100 - failedPct}%`,
                    backgroundColor: color,
                  }}
                />
                <div
                  className="h-full bg-red-500"
                  style={{ width: `${failedPct}%` }}
                />
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
